/**
 * The astro-journal and the accuracy loop.
 *
 * The journal is the user's own record: notes, life events, readings they kept,
 * and the verdicts they gave predictions. Accuracy ratings are what make the
 * personal profile visibly grow — the user grades individual SIGNALS, and the
 * rolled-up stats show which system, which life area and which period length
 * have actually landed for them.
 *
 * Honest framing (house rule): a hit rate is a record of how a reader FELT a
 * lens fitted their own life. It is not evidence that astrology predicts
 * events, and no surface may present it as one.
 *
 * Privacy: journal bodies are the most personal text in the product. They are
 * owner-read/owner-write only, never fed to matching, and only sent to the AI
 * when the user explicitly attaches an entry to a conversation.
 */

import type { LifeEventType } from './astrology.js';
import { ASTRO_SYSTEMS, HOROSCOPE_PERIODS, LIFE_AREAS } from './horoscope.js';
import type { AstroSystem, HoroscopePeriod, LifeArea } from './horoscope.js';

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

export type JournalEntryKind =
  /** Free-form writing by the user. */
  | 'note'
  /** Something that happened, datable — also usable as rectification input. */
  | 'life-event'
  /** A reading the user kept, so it can be looked back on. */
  | 'reading-saved'
  /** Auto-written when the user grades a signal, so the timeline stays whole. */
  | 'prediction-rated';

export const JOURNAL_ENTRY_KINDS: JournalEntryKind[] = [
  'note',
  'life-event',
  'reading-saved',
  'prediction-rated',
];

/** 1 = worst, 3 = neutral, 5 = best. Deliberately coarse; not a clinical scale. */
export type Mood = 1 | 2 | 3 | 4 | 5;

export interface JournalEntry {
  id: string;
  uid: string;
  /** When the entry is ABOUT, not when it was typed — users backdate life events. */
  atISO: string;
  kind: JournalEntryKind;
  title: string;
  body?: string;
  /** Free-form user tags, lowercased. Never auto-populated from AI inference. */
  tags: string[];
  /** `HoroscopeReading.periodKey` this entry hangs off, when it hangs off one. */
  linkedReadingKey?: string;
  /** Which tradition `linkedReadingKey` belongs to — the key alone is ambiguous. */
  linkedSystem?: AstroSystem;
  /** `Signal.id` inside that reading, for entries about one specific signal. */
  linkedSignalId?: string;
  /**
   * Set on 'life-event' entries. Shares the vocabulary the rectification engine
   * already uses, so a journal the user kept anyway can refine their birth time.
   */
  lifeEventType?: LifeEventType;
  mood?: Mood;
  createdAtISO: string;
  updatedAtISO: string;
}

// ---------------------------------------------------------------------------
// Accuracy ratings
// ---------------------------------------------------------------------------

export type AccuracyVerdict =
  /** It described something real for them. */
  | 'hit'
  /** It did not. */
  | 'miss'
  /** Partly — right theme, wrong intensity or wrong area of life. */
  | 'partial'
  /** They could not tell either way. Excluded from the hit-rate denominator. */
  | 'unclear';

export const ACCURACY_VERDICTS: AccuracyVerdict[] = ['hit', 'miss', 'partial', 'unclear'];

/** Weight each verdict contributes to a hit rate. 'unclear' scores nothing and counts for nothing. */
export const VERDICT_CREDIT: Record<AccuracyVerdict, number> = {
  hit: 1,
  partial: 0.5,
  miss: 0,
  unclear: 0,
};

/**
 * One verdict on one signal. `${readingKey}_${signalId}` is the natural id —
 * a user grades a given signal in a given reading exactly once, and re-rating
 * overwrites rather than appending.
 */
export interface AccuracyRating {
  id: string;
  uid: string;
  /** `HoroscopeReading.periodKey`. */
  readingKey: string;
  /** `Signal.id`. */
  signalId: string;
  verdict: AccuracyVerdict;
  note?: string;
  /**
   * Denormalised from the rated reading/signal. Firestore cannot join, and
   * `AccuracyStats` is uncomputable without these three on the rating itself.
   */
  system: AstroSystem;
  period: HoroscopePeriod;
  area: LifeArea;
  ratedAtISO: string;
}

// ---------------------------------------------------------------------------
// Rolled-up stats
// ---------------------------------------------------------------------------

export interface AccuracyTally {
  /** Every rating in this bucket, including 'unclear'. */
  rated: number;
  hits: number;
  partials: number;
  misses: number;
  /**
   * (hits + partials/2) / (hits + partials + misses), 0..1.
   * 'unclear' is excluded from both sides; `rated - hits - partials - misses`
   * recovers how many there were. Zero when nothing decisive has been rated.
   */
  hitRate: number;
}

/**
 * Cached aggregate (one document per user). Ratings are canonical; this is a
 * mirror, recomputed server-side — the same relationship the credit ledger has
 * with the balance on `EntitlementDoc`.
 */
export interface AccuracyStats {
  uid: string;
  bySystem: Record<AstroSystem, AccuracyTally>;
  byArea: Record<LifeArea, AccuracyTally>;
  byPeriod: Record<HoroscopePeriod, AccuracyTally>;
  /** Consecutive local calendar days with at least one rating, ending today or yesterday. */
  streakDays: number;
  /** Local day key of the most recent rating — what `streakDays` is measured against. */
  lastRatedDayKey?: string;
  totalRated: number;
  updatedAtISO: string;
}

// ---------------------------------------------------------------------------
// Helpers — one implementation so client and server never disagree
// ---------------------------------------------------------------------------

export function emptyAccuracyTally(): AccuracyTally {
  return { rated: 0, hits: 0, partials: 0, misses: 0, hitRate: 0 };
}

/**
 * A zeroed stats document with every bucket key present, so the UI can render
 * the full grid on day zero instead of branching on undefined.
 */
export function emptyAccuracyStats(uid: string, updatedAtISO: string): AccuracyStats {
  const bySystem = {} as Record<AstroSystem, AccuracyTally>;
  for (const system of ASTRO_SYSTEMS) bySystem[system] = emptyAccuracyTally();
  const byArea = {} as Record<LifeArea, AccuracyTally>;
  for (const area of LIFE_AREAS) byArea[area] = emptyAccuracyTally();
  const byPeriod = {} as Record<HoroscopePeriod, AccuracyTally>;
  for (const period of HOROSCOPE_PERIODS) byPeriod[period] = emptyAccuracyTally();
  return { uid, bySystem, byArea, byPeriod, streakDays: 0, totalRated: 0, updatedAtISO };
}

/** Recompute `hitRate` from the counts. Returns 0 when nothing decisive was rated. */
export function accuracyHitRate(tally: Pick<AccuracyTally, 'hits' | 'partials' | 'misses'>): number {
  const decisive = tally.hits + tally.partials + tally.misses;
  if (decisive === 0) return 0;
  return (tally.hits * VERDICT_CREDIT.hit + tally.partials * VERDICT_CREDIT.partial) / decisive;
}

/** Fold one verdict into a tally, keeping `hitRate` consistent. */
export function applyVerdict(tally: AccuracyTally, verdict: AccuracyVerdict): AccuracyTally {
  const next: AccuracyTally = {
    rated: tally.rated + 1,
    hits: tally.hits + (verdict === 'hit' ? 1 : 0),
    partials: tally.partials + (verdict === 'partial' ? 1 : 0),
    misses: tally.misses + (verdict === 'miss' ? 1 : 0),
    hitRate: 0,
  };
  next.hitRate = accuracyHitRate(next);
  return next;
}
